import { Link } from "@tanstack/react-router";
import { CheckCircle2, ChevronRight, Phone, MessageCircle } from "lucide-react";
import { ProvincePageShell } from "@/components/ProvincePageShell";
import { ServiceKnowledgeLinks } from "@/components/ServiceKnowledgeLinks";
import { PageGallery } from "@/components/PageGallery";
import { SmartImage } from "@/components/SmartImage";
import { getServiceHero } from "@/content/service-pillars/hero-images";
import { FaqSourceNote, type FaqSourceTopic } from "@/components/FaqSourceNote";
import { SERVICE_LINKS, AREA_LINKS, SEGMENT_LINKS } from "@/lib/link-index.gen";
import { COMPANY } from "@/lib/company";
import { getActiveProvince } from "@/provinces/_registry";
import type { ServicePillar } from "@/content/service-pillars/types";
import type { PillarDepth } from "@/content/service-pillars/depth-shared";

function safeArray<T>(value: T[] | undefined | null): T[] {
  return Array.isArray(value) ? value : [];
}

/** Pillar category -> official-source topic used by FaqSourceNote. */
const SOURCE_TOPIC: Record<string, FaqSourceTopic> = {
  notary: "notary",
  translation: "translation",
  legalization: "consular",
  background: "police-clearance",
  "visa-marriage": "visa",
};

export function ServicePillarShell({
  pillar,
  depth,
}: {
  pillar: ServicePillar;
  /** Consultative depth for this slug, loaded lazily by the route loader. */
  depth?: PillarDepth | null;
}) {
  const { config } = getActiveProvince();
  const relatedServiceSlugs = safeArray(pillar.relatedServiceSlugs);
  const relatedAreaSlugs = safeArray(pillar.relatedAreaSlugs);
  const relatedSegmentSlugs = safeArray(pillar.relatedSegmentSlugs);
  const sections = safeArray(pillar.sections);
  const quickFacts = safeArray(pillar.quickFacts);
  const documents = safeArray(pillar.documents);
  const coverage = safeArray(pillar.coverage);
  const languages = safeArray(pillar.languages);
  const pricing = safeArray(pillar.pricing);
  const process = safeArray(pillar.process);
  const faqs = safeArray(pillar.faqs);
  const advice = safeArray(pillar.advice ?? depth?.advice);
  const cautions = safeArray(pillar.cautions ?? depth?.cautions);
  const caseStudies = safeArray(pillar.caseStudies ?? depth?.caseStudies);
  const consultingNote = pillar.consultingNote ?? depth?.consultingNote;
  const comparisonSrc = pillar.comparison ?? depth?.comparison;
  const comparison = comparisonSrc && comparisonSrc.rows.length > 0 ? comparisonSrc : null;
  const related = SERVICE_LINKS.filter((s) => relatedServiceSlugs.includes(s.slug));
  // Same-category siblings (excluding this page and anything already listed above)
  // so no pillar page is ever a dead end for crawlers or readers.
  const siblings = SERVICE_LINKS.filter(
    (s) =>
      s.category === pillar.category &&
      s.slug !== pillar.slug &&
      !relatedServiceSlugs.includes(s.slug),
  ).slice(0, 12);
  const sourceTopic = SOURCE_TOPIC[pillar.category] ?? "notary";
  const areas = AREA_LINKS.filter((a) => relatedAreaSlugs.includes(a.slug));
  const segments = SEGMENT_LINKS.filter((s) => relatedSegmentSlugs.includes(s.slug));
  return (
    <ProvincePageShell
      eyebrow={pillar.heroEyebrow}
      title={pillar.h1}
      subtitle={pillar.heroSubtitle}
      breadcrumb={[{ label: "Home", to: "/" }, { label: "บริการ", to: "/services" }, { label: pillar.h1.split("—")[0].trim() }]}
    >
      <div className="mx-auto max-w-5xl space-y-16 px-4 py-16">
        {/* Hero photograph (LCP) — real, content-matching visual per service category */}
        <figure className="-mt-6 overflow-hidden rounded-2xl border border-border">
          <SmartImage
            src={getServiceHero(pillar.category).src}
            alt={getServiceHero(pillar.category).altTh}
            width={1200}
            height={630}
            priority
            className="h-auto w-full object-cover"
          />
        </figure>
        {/* TL;DR */}
        <section id="ai-tldr" className="speakable rounded-2xl border-2 border-gold/40 bg-gold/5 p-6">
          <div className="text-[11px] font-bold uppercase tracking-widest text-gold">Thai Notary Law & Service Co., Ltd.</div>
          <p className="mt-3 text-base leading-relaxed text-foreground">{pillar.tldr}</p>
        </section>

        {/* Quick facts */}
        <section>
          <h2 className="text-2xl font-black text-primary">ข้อมูลด่วน</h2>
          <dl className="mt-5 grid gap-3 md:grid-cols-2">
            {quickFacts.map((f) => (
              <div key={f.label} className="rounded-xl border border-border bg-card p-4">
                <dt className="text-[11px] font-bold uppercase tracking-widest text-gold">{f.label}</dt>
                <dd className="mt-1 text-sm font-medium text-foreground">{f.value}</dd>
              </div>
            ))}
          </dl>
        </section>

        {/* TOC */}
        <nav aria-label="สารบัญ" className="rounded-2xl border border-border bg-secondary/40 p-5">
          <div className="text-[11px] font-bold uppercase tracking-widest text-gold">สารบัญหน้านี้</div>
          <ol className="mt-3 grid list-decimal gap-1.5 pl-5 text-sm text-foreground md:grid-cols-2">
            {sections.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`} className="hover:text-gold">{s.h2}</a>
              </li>
            ))}
            <li><a href="#documents" className="hover:text-gold">ชนิดเอกสารที่ครอบคลุม</a></li>
            <li><a href="#pricing" className="hover:text-gold">ราคา</a></li>
            <li><a href="#process" className="hover:text-gold">ขั้นตอน</a></li>
            {comparison && <li><a href="#comparison" className="hover:text-gold">{comparison.title}</a></li>}
            {advice.length > 0 && <li><a href="#advice" className="hover:text-gold">คำแนะนำก่อนเริ่มดำเนินการ</a></li>}
            {cautions.length > 0 && <li><a href="#cautions" className="hover:text-gold">ข้อควรระวัง / สาเหตุที่เอกสารถูกตีกลับ</a></li>}
            {caseStudies.length > 0 && <li><a href="#case-studies" className="hover:text-gold">กรณีศึกษาจากงานจริง</a></li>}
            <li><a href="#faq" className="hover:text-gold">คำถามที่พบบ่อย</a></li>
          </ol>
        </nav>

        {/* Long-form sections */}
        {sections.map((sec) => (
          <section key={sec.id} id={sec.id} className="scroll-mt-24">
            <h2 className="text-2xl font-black text-primary md:text-3xl">{sec.h2}</h2>
            <div className="mt-5 space-y-4 text-base leading-relaxed text-foreground">
              {sec.body.map((p, i) => <p key={i}>{p}</p>)}
            </div>
            {sec.bullets && (
              <ul className="mt-5 space-y-2.5">
                {sec.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2.5 rounded-lg border border-border bg-card p-3.5 text-sm">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
            {sec.callout && (
              <div className="mt-5 rounded-xl border-l-4 border-gold bg-gold/10 p-4 text-sm font-medium text-foreground">
                💡 {sec.callout}
              </div>
            )}
          </section>
        ))}

        {/* Documents */}
        <section id="documents" className="scroll-mt-24">
          <h2 className="text-2xl font-black text-primary md:text-3xl">ชนิดเอกสารที่ครอบคลุม</h2>
          <p className="mt-3 text-sm text-muted-foreground">รวมทุกประเภทเอกสารที่เราเคยให้บริการใน{config.nameTh} — หากไม่อยู่ในรายการ โทรสอบถามได้</p>
          <div className="mt-6 grid gap-5 md:grid-cols-2">
            {documents.map((d) => (
              <div key={d.group} className="rounded-2xl border border-border bg-card p-5">
                <h3 className="text-sm font-extrabold text-primary">{d.group}</h3>
                <ul className="mt-3 space-y-1.5 text-sm text-foreground">
                  {d.items.map((it) => <li key={it} className="flex gap-2"><span className="text-gold">•</span> {it}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Visual evidence (real work samples) */}
        <PageGallery category={pillar.category} seed={pillar.slug} locale="th" />

        {/* Coverage */}
        {coverage.length > 0 && (
          <section id="coverage" className="scroll-mt-24">
            <h2 className="text-2xl font-black text-primary md:text-3xl">พื้นที่/ประเทศที่ครอบคลุม</h2>
            <div className="mt-6 grid gap-5 md:grid-cols-2">
              {coverage.map((c) => (
                <div key={c.group} className="rounded-2xl border border-border bg-card p-5">
                  <h3 className="text-sm font-extrabold text-primary">{c.group}</h3>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {c.items.map((it) => <span key={it} className="rounded-md bg-secondary px-2.5 py-1 text-xs">{it}</span>)}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Languages */}
        {languages.length > 0 && (
          <section>
            <h2 className="text-2xl font-black text-primary md:text-3xl">ภาษาที่รองรับ</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {languages.map((l) => <span key={l} className="rounded-md border border-gold/40 bg-gold/10 px-3 py-1.5 text-xs font-semibold text-gold">{l}</span>)}
            </div>
          </section>
        )}

        {/* Pricing */}
        <section id="pricing" className="scroll-mt-24">
          <h2 className="text-2xl font-black text-primary md:text-3xl">ตารางราคา</h2>
          <div className="mt-5 overflow-hidden rounded-2xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-foreground">
                <tr>
                  <th className="px-4 py-3 text-left font-bold">รายการ</th>
                  <th className="px-4 py-3 text-left font-bold">ราคา</th>
                  <th className="px-4 py-3 text-left font-bold">หมายเหตุ</th>
                </tr>
              </thead>
              <tbody>
                {pricing.map((p, i) => (
                  <tr key={p.item} className={i % 2 ? "bg-background" : "bg-card"}>
                    <td className="px-4 py-3 align-top font-medium text-foreground">{p.item}</td>
                    <td className="px-4 py-3 align-top font-bold text-gold">{p.price}</td>
                    <td className="px-4 py-3 align-top text-xs text-muted-foreground">{p.note ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Process */}
        <section id="process" className="scroll-mt-24">
          <h2 className="text-2xl font-black text-primary md:text-3xl">ขั้นตอนการใช้บริการ</h2>
          <ol className="mt-6 space-y-4">
            {process.map((p) => (
              <li key={p.step} className="rounded-2xl border border-border bg-card p-5">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-gold text-sm font-black text-gold-foreground">{p.step}</span>
                  <h3 className="text-base font-bold text-primary">{p.title}</h3>
                  {p.duration && <span className="ml-auto text-xs font-semibold text-muted-foreground">⏱ {p.duration}</span>}
                </div>
                <p className="mt-3 text-sm leading-relaxed text-foreground">{p.body}</p>
              </li>
            ))}
          </ol>
        </section>

        {comparison && (
          <section id="comparison" className="scroll-mt-24">
            <h2 className="text-2xl font-black text-primary md:text-3xl">{comparison.title}</h2>
            {comparison.intro && (
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{comparison.intro}</p>
            )}
            <div className="mt-5 overflow-x-auto rounded-2xl border border-border">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-foreground">
                  <tr>
                    {comparison.columns.map((c) => (
                      <th key={c} className="px-4 py-3 text-left font-bold">{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {comparison.rows.map((row, i) => (
                    <tr key={row[0]} className={i % 2 ? "bg-background" : "bg-card"}>
                      {row.map((cell, j) => (
                        <td
                          key={j}
                          className={
                            j === 0
                              ? "px-4 py-3 align-top font-semibold text-foreground"
                              : "px-4 py-3 align-top text-muted-foreground"
                          }
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {comparison.verdict && (
              <div className="mt-4 rounded-xl border-l-4 border-gold bg-gold/10 p-4 text-sm font-medium text-foreground">
                {comparison.verdict}
              </div>
            )}
          </section>
        )}

        {advice.length > 0 && (
          <section id="advice" className="scroll-mt-24">
            <h2 className="text-2xl font-black text-primary md:text-3xl">คำแนะนำก่อนเริ่มดำเนินการ</h2>
            <ol className="mt-5 space-y-2.5 text-sm leading-relaxed">
              {advice.map((a, i) => (
                <li key={i} className="flex items-start gap-2.5 rounded-lg border border-border bg-card p-3.5">
                  <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gold text-xs font-black text-gold-foreground">{i + 1}</span>
                  <span>{a}</span>
                </li>
              ))}
            </ol>
          </section>
        )}

        {cautions.length > 0 && (
          <section id="cautions" className="scroll-mt-24">
            <h2 className="text-2xl font-black text-primary md:text-3xl">ข้อควรระวัง / สาเหตุที่เอกสารถูกตีกลับ</h2>
            <ul className="mt-5 space-y-2.5 text-sm leading-relaxed">
              {cautions.map((c, i) => (
                <li key={i} className="flex items-start gap-2.5 rounded-lg border-l-4 border-destructive/60 bg-destructive/5 p-3.5">
                  <span className="font-bold text-destructive">!</span>
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {caseStudies.length > 0 && (
          <section id="case-studies" className="scroll-mt-24">
            <h2 className="text-2xl font-black text-primary md:text-3xl">กรณีศึกษาจากงานจริง</h2>
            <p className="mt-3 text-sm text-muted-foreground">
              ตัวอย่างเชิงกระบวนการจากงานที่ทีมเราดูแล — ปรับรายละเอียดเพื่อรักษาความเป็นส่วนตัวของผู้ใช้บริการ
            </p>
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              {caseStudies.map((cs) => (
                <article key={cs.title} className="rounded-2xl border border-border bg-card p-5">
                  <h3 className="text-sm font-extrabold text-primary">{cs.title}</h3>
                  <dl className="mt-3 space-y-2 text-sm leading-relaxed">
                    <div>
                      <dt className="text-[11px] font-bold uppercase tracking-widest text-gold">โจทย์</dt>
                      <dd className="text-foreground">{cs.situation}</dd>
                    </div>
                    <div>
                      <dt className="text-[11px] font-bold uppercase tracking-widest text-gold">สิ่งที่เราทำ</dt>
                      <dd className="text-foreground">{cs.action}</dd>
                    </div>
                    <div>
                      <dt className="text-[11px] font-bold uppercase tracking-widest text-gold">ผลลัพธ์</dt>
                      <dd className="text-foreground">{cs.outcome}</dd>
                    </div>
                    {cs.takeaway && (
                      <div>
                        <dt className="text-[11px] font-bold uppercase tracking-widest text-gold">บทเรียน</dt>
                        <dd className="text-muted-foreground">{cs.takeaway}</dd>
                      </div>
                    )}
                  </dl>
                </article>
              ))}
            </div>
          </section>
        )}

        {consultingNote && (
          <section id="consulting" className="scroll-mt-24 rounded-2xl border-2 border-gold/40 bg-gold/5 p-6">
            <h2 className="text-2xl font-black text-primary md:text-3xl">เราไม่ใช่แค่ผู้รับทำ — เราเป็นที่ปรึกษาของคุณ</h2>
            <p className="mt-3 text-base leading-relaxed text-foreground">{consultingNote}</p>
          </section>
        )}

        {/* Province Areas */}
        {areas.length > 0 && (
          <section>
            <h2 className="text-2xl font-black text-primary md:text-3xl">พื้นที่ใน{config.nameTh}ที่ให้บริการบ่อย</h2>
            <div className="mt-5 grid gap-3 md:grid-cols-3">
              {areas.map((a) => (
                <Link key={a.slug} to="/areas/$area" params={{ area: a.slug }} className="group rounded-xl border border-border bg-card p-4 hover:border-gold">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-gold">{a.districtTh}</div>
                  <h3 className="mt-1 text-sm font-bold text-primary group-hover:text-gold">{a.nameTh} — {a.nameEn}</h3>
                  <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-bold text-gold">ดูพื้นที่ <ChevronRight className="h-3 w-3" /></div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Segments */}
        {segments.length > 0 && (
          <section>
            <h2 className="text-2xl font-black text-primary md:text-3xl">เหมาะกับกลุ่มลูกค้า</h2>
            <div className="mt-5 grid gap-3 md:grid-cols-3">
              {segments.map((seg) => (
                <Link key={seg.slug} to="/for/$segment" params={{ segment: seg.slug }} className="group rounded-xl border border-border bg-card p-4 hover:border-gold">
                  <div className="text-2xl">{seg.emoji}</div>
                  <h3 className="mt-2 text-sm font-bold text-primary group-hover:text-gold">{seg.titleTh}</h3>
                  <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{seg.shortTh}</p>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* FAQs */}
        <section id="faq" className="scroll-mt-24">
          <h2 className="text-2xl font-black text-primary md:text-3xl">คำถามที่พบบ่อย</h2>
          <div className="mt-5 space-y-3">
            {faqs.map((f) => (
              <details key={f.q} className="group rounded-xl border border-border bg-card p-4 open:border-gold">
                <summary className="cursor-pointer text-sm font-bold text-primary">{f.q}</summary>
                <p className="mt-3 text-sm leading-relaxed text-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Related services */}
        {related.length > 0 && (
          <section>
            <h2 className="text-2xl font-black text-primary md:text-3xl">บริการที่เกี่ยวข้อง</h2>
            <div className="mt-5 grid gap-3 md:grid-cols-3">
              {related.map((r) => (
                <Link key={r.slug} to="/services/$service" params={{ service: r.slug }} className="group rounded-xl border border-border bg-card p-4 hover:border-gold">
                  <h3 className="text-sm font-bold text-primary group-hover:text-gold">{r.title}</h3>
                  <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-bold text-gold">ดูบริการ <ChevronRight className="h-3 w-3" /></div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Final CTA */}
        <ServiceKnowledgeLinks
          category={pillar.category}
          siblings={siblings.map((s) => ({ slug: s.slug, title: s.title }))}
        />

        {/* Official external sources (E-E-A-T) */}
        <FaqSourceNote topic={sourceTopic} locale="th" />

        <section className="rounded-3xl p-8 text-center text-primary-foreground md:p-12" style={{ background: "var(--gradient-hero)" }}>
          <h2 className="text-2xl font-black md:text-3xl">{pillar.finalCta}</h2>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a href={`tel:${COMPANY.phoneIntl}`} className="inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-bold text-gold-foreground shadow-[var(--shadow-gold-glow)]" style={{ background: "var(--gradient-gold)" }}>
              <Phone className="h-4 w-4" /> {COMPANY.phone}
            </a>
            <a href={`https://line.me/R/ti/p/${COMPANY.lineId}`} target="_blank" rel="noopener" className="inline-flex items-center gap-2 rounded-xl border-2 border-gold px-5 py-3 text-sm font-bold text-gold">
              <MessageCircle className="h-4 w-4" /> LINE {COMPANY.lineId}
            </a>
          </div>
        </section>
      </div>
    </ProvincePageShell>
  );
}